import { useEffect } from "react";

interface Props {}

const FaqPage = ({}: Props) => {
  return (
    <>
      <title>FAQ | Tuttobanana</title>
      <meta
        name="description"
        content="Got questions? We've got answers. Learn how Tuttobanana works, why bananas, and why joy is just one peel away."
      />

      <div className="bg-gray-100 text-gray-900 px-6 py-20 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-12 text-center text-yellow-500">
            Tuttobanana FAQ
          </h1>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            What It Is &amp; Why It Exists
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                What is a Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                A Tuttobanana is a tiny digital gift - a fun way to send
                appreciation, encouragement, or just a smile to someone you care
                about. Think of it as a little emotional hug delivered online.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Why bananas?
              </dt>
              <dd className="text-gray-700 mt-2">
                Bananas are fun, universal, and instantly recognizable. Plus,
                they make people smile. We're redefining “banana” as a verb - to
                banana someone means to share love, appreciation, or
                congratulations.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Why am I getting a Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                Because someone thought of you! Tuttobanana is about
                recognition, gratitude, and joy. A friend, colleague, or family
                member wanted to brighten your day.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            Trust, Safety &amp; Privacy
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Is Tuttobanana safe to click?
              </dt>
              <dd className="text-gray-700 mt-2">
                Yes. Every Tuttobanana comes directly from our official site
                (tuttobanana.com). No downloads, no spam, no tricks - just a
                playful digital gift.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                How do I know this isn't spam?
              </dt>
              <dd className="text-gray-700 mt-2">
                Every Tuttobanana comes from a real person - someone you know
                chose to banana you. All bananas are hosted at tuttobanana.com
                and carry our official branding.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Do Tuttobananas contain viruses or downloads?
              </dt>
              <dd className="text-gray-700 mt-2">
                Nope. A Tuttobanana is just a playful web animation with a
                message - nothing to install, nothing sneaky.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Do you sell my data or track me across the web?
              </dt>
              <dd className="text-gray-700 mt-2">
                No. We only log simple banana stats like how far your banana
                traveled. Your personal data stays yours - we never sell it.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                How do I know a Tuttobanana really came from my friend?
              </dt>
              <dd className="text-gray-700 mt-2">
                Every banana is linked to the sender's message and location. If
                you got one, it's because someone you know chose to send it.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            How It Works
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Do I need to create an account to use Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                <strong>No account needed</strong> to open a Tuttobanana, Banana it Forward, or
                Banana it Back. <br />
                <span className="block mt-2">
                  <strong>Tossing a New Banana</strong> (starting fresh with
                  your own design and message) does require a free account.
                </span>
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                What happens when I click the link?
              </dt>
              <dd className="text-gray-700 mt-2">
                You'll see a fun animation, a personalized message from the
                sender, and four options:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>
                    <strong>Banana it Forward</strong> → share the same banana
                    with someone new.
                  </li>
                  <li>
                    <strong>Banana it Back</strong> → send one back to the
                    original sender.
                  </li>
                  <li>
                    <strong>Toss a New Banana</strong> → create your own
                    (requires an account).
                  </li>
                  <li>
                    <strong>Visit Tuttobanana.com</strong> → learn more about
                    us.
                  </li>
                </ul>
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Do I have to download an app?
              </dt>
              <dd className="text-gray-700 mt-2">
                No app required. Tuttobanana works right in your browser - just
                click the link and unwrap your banana.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Can I send a Tuttobanana to anyone?
              </dt>
              <dd className="text-gray-700 mt-2">
                Yes! You can share them by link, text, email, QR code… wherever
                you can share a link, you can toss a banana.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                What if the person I send to doesn't open it?
              </dt>
              <dd className="text-gray-700 mt-2">
                Nothing bad happens. They'll just miss out on your banana! You
                can always toss another to someone else.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            Use Cases &amp; Value
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                What are some ways people use Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    Saying “Thank You” to a teacher, coach, or coworker.
                  </li>
                  <li>
                    Sending encouragement before a big test, game, or meeting.
                  </li>
                  <li>
                    Celebrating milestones like birthdays, promotions, or
                    graduations.
                  </li>
                  <li>
                    Just surprising a friend with a smile, for no reason at
                    all.
                  </li>
                </ul>
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Can businesses or groups use Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                Absolutely. Teams use Tuttobananas to recognize coworkers,
                coaches use them to encourage athletes, and parents use them to
                thank teachers. Any group that wants to share appreciation in a
                quick, delightful way can use Tuttobanana.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Can Tuttobananas go viral?
              </dt>
              <dd className="text-gray-700 mt-2">
                Yes! Every banana can be forwarded, tossed back, or re-sent.
                Some bananas travel across cities, countries, even continents.
                That's the fun of the Bananaverse.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Is this just a fad or does it have lasting value?
              </dt>
              <dd className="text-gray-700 mt-2">
                Like bananas themselves, it's simple, universal, and always
                makes people smile. Tuttobanana is designed to be habit-forming
                in a good way - quick, free bursts of joy you'll want to keep
                sharing.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            Cost &amp; Customization
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Does it cost anything to use Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                No. All bananas are free to send during launch. In the future,
                we may add optional premium features (like custom designs), but
                sharing Tuttobananas will always stay fun and easy.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Can I customize my bananas?
              </dt>
              <dd className="text-gray-700 mt-2">
                Right now you can personalize the message. Soon we'll be adding
                custom designs, themes, and premium banana types.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-gray-900">
            About Tuttobanana
          </h2>
          <dl className="space-y-10">
            <div>
              <dt className="text-xl font-semibold text-gray-900">
                Who created Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                We're a small team passionate about spreading joy in a quirky
                new way. Think of us as the banana stand for the digital age.
              </dd>
            </div>

            <div>
              <dt className="text-xl font-semibold text-gray-900">
                How do I contact someone at Tuttobanana?
              </dt>
              <dd className="text-gray-700 mt-2">
                You can reach us anytime at <strong>hello@tuttobanana.com</strong>.
              </dd>
            </div>
          </dl>

          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-6 text-yellow-500">
            Who Can Use Tuttobananas?
          </h2>
          <p className="text-gray-700 mb-6">
            Tuttobananas are for anyone who wants to brighten someone's day in
            seconds. A Tuttobanana is a tiny digital gift - free to send, fun to
            unwrap, and full of joy. Here are some of the ways people are
            already using them:
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                🍌 Everyday Appreciation
              </h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>
                  <strong>Friends &amp; Family</strong> - Send a quick “thinking
                  of you” or “love you” banana that makes someone smile
                  instantly.
                </li>
                <li>
                  <strong>Coworkers</strong> - Say thanks for helping out on a
                  project without writing a long email.
                </li>
                <li>
                  <strong>Neighbors &amp; Community</strong> - Welcome someone
                  new to the block or thank a friend for lending a hand.
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                💛 Encouragement &amp; Motivation
              </h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>
                  <strong>Students</strong> - A parent bananas encouragement
                  before a big test or recital.
                </li>
                <li>
                  <strong>Athletes</strong> - Coaches send post-game bananas for
                  hustle and effort.
                </li>
                <li>
                  <strong>Friends</strong> - Give a confidence boost before a
                  big presentation, interview, or life moment.
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                🎉 Celebrations &amp; Milestones
              </h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>
                  <strong>Birthdays</strong> - A quirky surprise that shows up
                  right in someone's inbox.
                </li>
                <li>
                  <strong>Achievements</strong> - Congratulate a teammate on a
                  promotion, a classmate on graduation, or a friend who just hit
                  a goal.
                </li>
                <li>
                  <strong>Everyday Wins</strong> - Finished a workout? Cooked a
                  great meal? Survived Monday? Banana it!
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                🙏 Recognition &amp; Thanks
              </h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>
                  <strong>Teachers &amp; Mentors</strong> - Students and parents
                  banana gratitude to those who make a difference.
                </li>
                <li>
                  <strong>Healthcare Workers</strong> - A heartfelt “thank you”
                  banana goes a long way.
                </li>
                <li>
                  <strong>Volunteers</strong> - Recognize the people giving
                  their time and energy to help others.
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900">😄 Just for Fun</h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>
                  <strong>Inside Jokes</strong> - Turn your shared humor into a
                  personalized banana.
                </li>
                <li>
                  <strong>Banana It Forward Chains</strong> - Watch a banana
                  travel across friends, families, and even continents.
                </li>
                <li>
                  <strong>Random Acts of Joy</strong> - Send one to someone who
                  least expects it, just because.
                </li>
              </ul>
            </div>

            <p className="text-gray-700">
              ✨ In short: Tuttobananas are for anyone, anytime, anywhere. When
              words aren't quite enough, banana it. <strong>Banana it forward!</strong>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default FaqPage;
